/**
 * cad-plugin — the `/cad` CAD surface as a studio-sibling `ComposableGenomePlugin`.
 *
 * #S-10 Phase 1b: the bespoke R3F workspace (`page('cad')` in the design) composed
 * into the ONE studio genome. Mirrors `agentPlugin`/`ideShellPlugin`: one
 * `component` editor (`workspace`) routed at `/cad`, lowering onto `component`/
 * `route` only (NO new EntityKind — gate 4 stays green, the route count derives).
 *
 * The editor is `React.lazy` so the whole CAD surface — three.js + R3F + drei —
 * stays in a split chunk OUT of the app entry (gate 8 — entry < bundle budget).
 *
 * @module @ubiclouds/v5-studio/cad/cad-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import { registerSurfaceCommand } from '@ubiclouds/v5-workbench/surface-commands';
import { CAD_LOAD_DOCUMENT } from '@ubiclouds/v5-cad-kernel/commands';

/** The CAD surface plugin id. */
export const CAD_PLUGIN_ID = 'v5-studio-cad';

/** The split-chunk R3F CAD workspace (three.js stays out of the entry). */
const LazyCadWorkspace = lazy(() => import('./CadWorkspace.js'));

/**
 * The CAD plugin — the `/cad` route → the lazy R3F workspace editor. Composed as
 * a SIBLING of `agentPlugin` in `studioPlugins`.
 */
export const cadPlugin: ComposableGenomePlugin = definePlugin(CAD_PLUGIN_ID)
  .name('CAD')
  .trustTier('core')
  .editor('workspace', LazyCadWorkspace)
  .route('/cad', { routesTo: 'component/workspace', title: 'CAD' })
  .build();

// #1527 — the CAD surface's side of the tier-1 command bus. Registered HERE, in
// the eagerly-loaded plugin module, rather than on surface mount: the call this
// replaces reached a MODULE-LEVEL zustand store, so it worked on a COLD click
// with `/cad` never opened, and registering on mount would have silently
// regressed exactly that path while every warm test kept passing.
//
// The store itself stays LAZY (it drags the R3F workspace behind it, which is the
// whole reason `/cad` is a split chunk), so the handler dynamic-imports it. The
// document therefore lands one microtask after the click instead of synchronously
// — the caller navigates to `/cad` regardless, and the surface reads the store on
// mount, so the observable order is unchanged.
registerSurfaceCommand(CAD_LOAD_DOCUMENT, (model) => {
  void import('./cad-store.js').then(({ useCadStore }) => {
    useCadStore.getState().loadDocument(model);
  });
});
